import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FiSave, FiX } from 'react-icons/fi';
import visitService from '../../services/visitService';
import { VISIT_STATUS, VISIT_TYPES } from '../../utils/constants';
import { common as commonMessages } from '../../utils/validationMessages';
import Spinner from '../Common/Spinner';
import Alert from '../Common/Alert';
import './VisitForm.css';

const VisitForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;

  const [formData, setFormData] = useState({
    siteID: '',
    visitType: '',
    craID: '',
    plannedStartDate: '',
    plannedEndDate: '',
    expectedDuration: '',
    visitObjective: '',
    notes: '',
    visitStatus: VISIT_STATUS.DRAFT,
  });

  const [loading, setLoading] = useState(isEdit);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [sites, setSites] = useState([]);

  useEffect(() => {
    if (isEdit) {
      fetchVisit();
    }
    // fetchSites();
  }, [id]);

  const fetchVisit = async () => {
    try {
      const visit = await visitService.getById(id);
      setFormData(visit);
      setError(null);
    } catch (err) {
      setError('Failed to load visit details');
    } finally {
      setLoading(false);
    }
  };

  // const fetchSites = async () => {
  //   // TODO: Implement API calls to get sites
  //   setSites([
  //     { siteID: 1, siteName: 'MH-ONCO-001' },
  //     { siteID: 2, siteName: 'AH-CARD-005' },
  //   ]);
  // };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      setSubmitting(true);
      setError(null);

      if (isEdit) {
        await visitService.update(id, formData);
      } else {
        await visitService.create(formData);
      }

      setSuccess(true);
      setTimeout(() => {
        navigate('/visits');
      }, 1000);
    } catch (err) {
      const data = err.response?.data;
      const fromErrors = data?.errors && typeof data.errors === 'object'
        ? Object.values(data.errors).flat().find((x) => typeof x === 'string' && x)
        : null;
      const msg = data?.error || data?.message || fromErrors || err.message || 'Failed to save visit';
      setError(msg);
      console.error('Error saving visit:', err);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <Spinner />;

  return (
    <div className="visit-form">
      <div className="form-header">
        <h2>{isEdit ? 'Edit Visit' : 'Schedule New Visit'}</h2>
      </div>

      {error && <Alert type="error" message={error} />}
      {success && <Alert type="success" message={commonMessages.authSuccess} />}

      <form onSubmit={handleSubmit} className="form">
        <div className="form-section">
          <h3>Basic Information</h3>

          <div className="form-grid">
            <div className="form-group">
              <label>Site *</label>
              <select
                name="siteID"
                value={formData.siteID}
                onChange={handleChange}
                required
              >
                <option value="">Select Site</option>
                {sites.map(site => (
                  <option key={site.siteID} value={site.siteID}>
                    {site.siteName}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Visit Type *</label>
              <select
                name="visitType"
                value={formData.visitType}
                onChange={handleChange}
                required
              >
                <option value="">Select Visit Type</option>
                {Object.entries(VISIT_TYPES).map(([key, value]) => (
                  <option key={key} value={key}>
                    {value}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>CRA *</label>
              <input
                type="text"
                name="craID"
                value={formData.craID}
                onChange={handleChange}
                required
              />
            </div>
          </div>
        </div>

        <div className="form-section">
          <h3>Schedule Details</h3>

          <div className="form-grid">
            <div className="form-group">
              <label>Scheduled Start Date <span class="required-asterisk">*</span></label>
              <input
                type="date"
                name="plannedStartDate"
                value={formData.plannedStartDate}
                onChange={handleChange}
                required
              />
            </div>

            <div className="form-group">
              <label>Scheduled Start Date *</label>
              <input
                type="date"
                name="plannedEndDate"
                value={formData.plannedEndDate}
                onChange={handleChange}
                required
              />
            </div>

            <div className="form-group">
              <label>Expected Duration (hours)</label>
              <input
                type="number"
                name="expectedDuration"
                value={formData.expectedDuration}
                onChange={handleChange}
                min="0"
              />
            </div>

            <div className="form-group">
              <label>Visit Status</label>
              <select
                name="visitStatus"
                value={formData.visitStatus}
                onChange={handleChange}
              >
                {Object.entries(VISIT_STATUS).map(([key, value]) => (
                  <option key={key} value={value}>
                    {value}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div className="form-section">
          <h3>Additional Details</h3>

          <div className="form-group">
            <label>Visit Objective</label>
            <textarea
              name="visitObjective"
              value={formData.visitObjective}
              onChange={handleChange}
              rows="4"
              placeholder="Enter visit objectives..."
            />
          </div>

          <div className="form-group">
            <label>Notes</label>
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              rows="4"
              maxLength={2000}
              placeholder="Enter Notes (max 2000 characters)..."
            />
          </div>
        </div>

        <div className="form-actions">
          <button
            type="submit"
            className="btn btn-primary"
            disabled={submitting}
          >
            <FiSave size={20} />
            {submitting ? 'Saving...' : 'Save Visit'}
          </button>

          <button
            type="button"
            className="btn btn-outline"
            onClick={() => navigate('/visits')}
          >
            <FiX size={20} />
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

export default VisitForm;